// -----------------------------------------------------------------------------
// storage/scratch.ts — local scratch storage for media blobs.
//
// Imported media is written here (keyed by asset id) so a saved project can be
// re-opened and its media re-hydrated without asking the user to re-pick files.
// It NEVER leaves the machine — this is the user's own browser storage.
//
// Preferred backend is OPFS (Origin Private File System: fast, file-based). When
// OPFS is unavailable we fall back to IndexedDB. The capability gate decides
// which (RuntimeConfig.useOpfs). (CLAUDE.md §3.)
// -----------------------------------------------------------------------------

const FILE_PREFIX = 'zf_';
const IDB_NAME = 'zeroframe-scratch';
const IDB_STORE = 'blobs';

export class ScratchStore {
  private rootPromise?: Promise<FileSystemDirectoryHandle>;
  private dbPromise?: Promise<IDBDatabase>;

  constructor(private readonly useOpfs: boolean) {}

  async put(key: string, blob: Blob): Promise<void> {
    if (this.useOpfs) {
      const root = await this.opfsRoot();
      const handle = await root.getFileHandle(FILE_PREFIX + key, { create: true });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    }
    const db = await this.idb();
    await idbRequest(db.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).put(blob, key));
  }

  async get(key: string): Promise<Blob | null> {
    try {
      if (this.useOpfs) {
        const root = await this.opfsRoot();
        const handle = await root.getFileHandle(FILE_PREFIX + key);
        return await handle.getFile();
      }
      const db = await this.idb();
      const result = await idbRequest<Blob | undefined>(
        db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key),
      );
      return result ?? null;
    } catch {
      // Missing key (or storage hiccup) → null, never throw at the call site.
      return null;
    }
  }

  async remove(key: string): Promise<void> {
    try {
      if (this.useOpfs) {
        const root = await this.opfsRoot();
        await root.removeEntry(FILE_PREFIX + key);
        return;
      }
      const db = await this.idb();
      await idbRequest(db.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).delete(key));
    } catch {
      /* already gone */
    }
  }

  private opfsRoot(): Promise<FileSystemDirectoryHandle> {
    if (!this.rootPromise) {
      // getDirectory() is not yet in every TS DOM lib; feature-detected upstream.
      this.rootPromise = (navigator.storage as { getDirectory(): Promise<FileSystemDirectoryHandle> })
        .getDirectory();
    }
    return this.rootPromise;
  }

  private idb(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
        const open = indexedDB.open(IDB_NAME, 1);
        open.onupgradeneeded = () => {
          if (!open.result.objectStoreNames.contains(IDB_STORE)) {
            open.result.createObjectStore(IDB_STORE);
          }
        };
        open.onsuccess = () => resolve(open.result);
        open.onerror = () => reject(open.error ?? new Error('IndexedDB open failed'));
      });
    }
    return this.dbPromise;
  }
}

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}
